import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import {
  SearchIcon,
  RefreshIcon,
  FileTextIcon,
  ClockIcon,
  BookOpenIcon
} from '../components/common/Icons';

interface Document {
  id: string;
  title: string;
  category: string;
  content: string;
  content_preview?: string;
  author: string;
  version: string | number;
  status: 'published' | 'draft' | 'archived';
  updated_at: string;
  lastUpdated?: string;
  last_modified?: string;
  path?: string;
  type?: string;
  word_count?: number;
  description?: string;
}

interface CategoryDef {
  id: string;
  label: string;
}

const CATEGORIES: CategoryDef[] = [
  { id: 'all', label: 'All' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'api', label: 'API Reference' },
  { id: 'security', label: 'Security' },
  { id: 'guides', label: 'Guides' },
  { id: 'tutorials', label: 'Tutorials' },
  { id: 'reference', label: 'Reference' },
  { id: 'changelog', label: 'Changelog' },
];

const DocsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    fetchDocuments();
  }, [authLoading]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/admin/docs');
      if (response.ok) {
        const data = await response.json();
        console.log('[DocsPage] Raw API response:', data);

        // Handle different response formats
        let docs: Document[] = Array.isArray(data) ? data : data.documents || [];

        // Filter out archived/deprecated docs if path contains those terms
        const relevantDocs = docs.filter((doc: Document) =>
          !doc.path?.includes('archive') &&
          !doc.path?.includes('deprecated') &&
          doc.status !== 'archived'
        );

        console.log('[DocsPage] Processed documents:', relevantDocs.length);
        setDocuments(relevantDocs);

        // Auto-select first doc if available
        if (relevantDocs.length > 0 && !selectedDoc) {
          setSelectedDoc(relevantDocs[0]);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch documentation');
      }
    } catch (err) {
      console.error('[DocsPage] Error:', err);
      setError('Failed to connect to API');
    }

    setIsLoading(false);
  };

  // Filter documents by category and search (title AND content)
  const filteredDocs = useMemo(() => {
    return documents.filter(doc => {
      // Category filter
      if (selectedCategory !== 'all' && doc.category !== selectedCategory) {
        return false;
      }

      // Search filter (title AND content)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const titleMatch = doc.title.toLowerCase().includes(query);
        const contentMatch = (doc.content || doc.content_preview || '').toLowerCase().includes(query);
        return titleMatch || contentMatch;
      }

      return true;
    });
  }, [documents, selectedCategory, searchQuery]);

  const formatVersion = (version: string | number): string => {
    if (typeof version === 'number') {
      return version.toFixed(1);
    }
    return String(version);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: userTimezone
      }).format(date);
    } catch {
      return dateStr;
    }
  };

  // Get available categories from the documents
  const availableCategories = useMemo(() => {
    const cats = new Set(documents.map(d => d.category));
    return CATEGORIES.filter(c => c.id === 'all' || cats.has(c.id));
  }, [documents]);

  // Handle document selection - fetch full content if needed
  const handleSelectDoc = async (doc: Document) => {
    // If document already has full content, just select it
    if (doc.content && doc.content.length > 100) {
      setSelectedDoc(doc);
      return;
    }

    // Fetch full document content
    try {
      const response = await authFetch(`/api/admin/docs/${doc.id}`);
      if (response.ok) {
        const fullDoc = await response.json();
        setSelectedDoc({
          ...doc,
          content: fullDoc.content,
          version: fullDoc.metadata?.version || doc.version,
          last_modified: fullDoc.metadata?.lastModified || doc.last_modified,
        });
      } else {
        // Fall back to original doc
        setSelectedDoc(doc);
      }
    } catch (err) {
      console.error('[DocsPage] Error fetching document:', err);
      setSelectedDoc(doc);
    }
  };

  return (
    <div className="page-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Documentation</h1>
          <p className="page-subtitle">Platform documentation and guides</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-ghost" onClick={fetchDocuments} title="Refresh">
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar - Document List */}
        <div
          className="docs-sidebar glass-card"
          style={{
            width: '320px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <div className="docs-search" style={{ marginBottom: '16px' }}>
            <div
              className="input-wrapper"
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                padding: '8px 12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <SearchIcon size={16} style={{ marginRight: '8px', opacity: 0.5 }} />
              <input
                type="text"
                placeholder="Search docs & content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text-primary)',
                  width: '100%',
                  fontSize: '14px',
                }}
              />
            </div>
          </div>

          {/* Category Tabs */}
          <div
            className="docs-categories"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '16px',
            }}
          >
            {availableCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background:
                    selectedCategory === cat.id
                      ? 'var(--primary)'
                      : 'rgba(255, 255, 255, 0.05)',
                  color:
                    selectedCategory === cat.id
                      ? 'white'
                      : 'var(--text-secondary)',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Document List */}
          <div
            className="docs-list"
            style={{
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {isLoading && (
              <div className="loading-state" style={{ padding: '2rem', textAlign: 'center' }}>
                <div className="spinner" />
                <p>Loading documents...</p>
              </div>
            )}

            {!isLoading && filteredDocs.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: 'var(--text-tertiary)',
                  padding: '2rem',
                }}
              >
                <FileTextIcon size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p>No documents found</p>
              </div>
            )}

            {filteredDocs.map((doc) => (
              <button
                key={doc.id}
                onClick={() => handleSelectDoc(doc)}
                className={`doc-item ${selectedDoc?.id === doc.id ? 'active' : ''}`}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  background:
                    selectedDoc?.id === doc.id
                      ? 'rgba(139, 92, 246, 0.15)'
                      : 'transparent',
                  borderLeft:
                    selectedDoc?.id === doc.id
                      ? '3px solid var(--primary)'
                      : '3px solid transparent',
                }}
              >
                <div
                  style={{
                    fontWeight: 500,
                    marginBottom: '4px',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                  }}
                >
                  {doc.title}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  <span
                    style={{
                      background: 'rgba(139, 92, 246, 0.15)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                    }}
                  >
                    {doc.category}
                  </span>
                  <span>v{formatVersion(doc.version)}</span>
                  <span>{formatDate(doc.updated_at || doc.lastUpdated || doc.last_modified || '')}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content - Document Viewer (Read-Only) */}
        <div
          className="docs-viewer glass-card"
          style={{
            flex: 1,
            padding: '24px',
            overflowY: 'auto',
          }}
        >
          {selectedDoc ? (
            <>
              <div className="doc-header" style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '24px', marginBottom: '8px', fontWeight: 600 }}>
                  {selectedDoc.title}
                </h1>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    color: 'var(--text-tertiary)',
                    fontSize: '14px',
                    alignItems: 'center',
                  }}
                >
                  <span>Version: {formatVersion(selectedDoc.version)}</span>
                  <span style={{ opacity: 0.5 }}>|</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ClockIcon size={14} />
                    {formatDate(selectedDoc.updated_at || selectedDoc.lastUpdated || selectedDoc.last_modified || '')}
                  </span>
                  <span style={{ opacity: 0.5 }}>|</span>
                  <span
                    className="category-badge"
                    style={{
                      background: 'rgba(139, 92, 246, 0.15)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {selectedDoc.category}
                  </span>
                  {selectedDoc.author && (
                    <>
                      <span style={{ opacity: 0.5 }}>|</span>
                      <span>By: {selectedDoc.author}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="doc-content prose" style={{
                lineHeight: 1.7,
                color: 'var(--text-primary)',
                fontSize: '15px',
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedDoc.content || selectedDoc.content_preview || 'No content available'}
                </ReactMarkdown>
              </div>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--text-tertiary)',
              }}
            >
              <BookOpenIcon size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p>Select a document to view</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .search-highlight {
          background: rgba(245, 158, 11, 0.3);
          padding: 0 2px;
          border-radius: 2px;
        }

        .prose {
          max-width: 100%;
          overflow-x: hidden;
        }

        .prose h1, .prose h2, .prose h3, .prose h4 {
          margin-top: 24px;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .prose h1 { font-size: 1.5rem; }
        .prose h2 { font-size: 1.25rem; }
        .prose h3 { font-size: 1.125rem; }

        .prose p {
          margin-bottom: 16px;
          word-wrap: break-word;
        }

        .prose code {
          background: rgba(0, 0, 0, 0.3);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'JetBrains Mono', 'Space Mono', monospace;
          font-size: 0.85em;
          word-wrap: break-word;
        }

        .prose pre {
          background: rgba(0, 0, 0, 0.3);
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 16px 0;
          max-width: 100%;
        }

        .prose pre code {
          background: transparent;
          padding: 0;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .prose ul, .prose ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }

        .prose li {
          margin-bottom: 8px;
        }

        .prose a {
          color: var(--primary);
          text-decoration: underline;
        }

        .prose blockquote {
          border-left: 3px solid var(--primary);
          padding-left: 16px;
          margin: 16px 0;
          color: var(--text-secondary);
          font-style: italic;
        }

        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 0.9em;
          overflow-x: auto;
          display: block;
        }

        .prose th, .prose td {
          padding: 10px 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          text-align: left;
        }

        .prose th {
          background: rgba(139, 92, 246, 0.15);
          font-weight: 600;
        }

        .prose tr:nth-child(even) {
          background: rgba(255, 255, 255, 0.02);
        }

        .prose hr {
          border: none;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin: 24px 0;
        }

        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default DocsPage;
